import { SDK_ERRORS } from '@riz/contracts/sdk';
import { rejectWith } from '../core/errors.ts';
import type { SdkClient } from '../core/client.ts';

export interface AnalyticsModule {
  track(eventName: string, properties?: Record<string, unknown>): Promise<Record<string, unknown>>;
  funnel(step: string, properties?: Record<string, unknown>): Promise<Record<string, unknown>>;
  tutorial: {
    start(id: string, details?: Record<string, unknown>): Promise<Record<string, unknown>>;
    complete(id: string, details?: Record<string, unknown>): Promise<Record<string, unknown>>;
  };
  checkpoint(id: string, details?: Record<string, unknown>): Promise<Record<string, unknown>>;
  item(id: string, details?: Record<string, unknown>): Promise<Record<string, unknown>>;
  powerup(id: string, details?: Record<string, unknown>): Promise<Record<string, unknown>>;
  level: {
    start(level: string | number, details?: Record<string, unknown>): Promise<Record<string, unknown>>;
    complete(level: string | number, details?: Record<string, unknown>): Promise<Record<string, unknown>>;
  };
}

export function createAnalyticsModule(client: SdkClient): AnalyticsModule {
  return {
    track(eventName, properties) {
      const capability = client.requireCapability<Record<string, unknown>>('analytics.custom', 'Custom analytics');
      if (capability) return capability;
      const safeName = slugifyEventName(eventName);
      if (!safeName) {
        return rejectWith(SDK_ERRORS.invalidPayload, 'An analytics event name is required.');
      }
      return client.track('custom_event', { ...properties, eventName: safeName });
    },

    funnel(step, properties) {
      const safeName = slugifyEventName(step);
      if (!safeName) {
        return rejectWith(SDK_ERRORS.invalidPayload, 'A funnel step name is required.');
      }
      return client.track('funnel_step', { ...properties, step: safeName });
    },

    tutorial: {
      start: (id, details) => client.track('tutorial_started', { ...details, tutorialId: id }),
      complete: (id, details) => client.track('tutorial_completed', { ...details, tutorialId: id }),
    },

    checkpoint: (id, details) => client.track('checkpoint_reached', { ...details, checkpointId: id }),
    item: (id, details) => client.track('item_collected', { ...details, itemId: id }),
    powerup: (id, details) => client.track('powerup_used', { ...details, powerupId: id }),

    level: {
      start: (level, details) => client.track('level_started', { ...details, level }),
      complete: (level, details) => client.track('level_completed', { ...details, level }),
    },
  };
}

/** Names are normalised client-side so a typo does not fragment a metric. */
export function slugifyEventName(value: unknown): string {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);
}
